// Copyright 2023 Autodesk, Inc.
// All rights reserved.
// This computer source code and related instructions and comments are the
// unpublished confidential and proprietary information of Autodesk, Inc.
// and are protected under Federal copyright and state trade secret law.
// They may not be disclosed to, copied or used by any third party without
// the prior written consent of Autodesk, Inc.


import { DtConstants } from "../schema/DtConstants";
import { ElementFlags, KeyFlags } from "../schema/dt-schema";
import * as et from '../../application/EventTypes';
import * as dte from '../DtEventTypes';
import {
  deserializeFacet,
  FacetTypes,
  getDefaultFacetList } from
"./Facets";

import { FacetVizEffects } from "./FacetVizEffects";
import { Prefs3D } from "../../application/PreferenceNames";
import { SelectionMode } from "../../wgs/scene/SelectionMode";
import { compareNumbers, sortedArrayIntersection } from "./sortedArray";
const RC = require("../resources/cats_enum.json");

const UNDEFINED = '(undefined)';
// Models is an immovable facet, and it is kept at this index.
const MODEL_FACET_DEFS_IDX = 0;

function smallArraysEqual(a, b) {
  //Definitely not a fast or robust implementation -- this only expects arrays with up to 3 elements
  //as input.
  if (!a) a = [];
  if (!b) b = [];

  return a.every((item) => b.includes(item)) && b.every((item) => a.includes(item));
}


export class FacetsManager {

  /**
   * @param {Object} options Application configuration options
   */
  constructor(facility, options = {}, wantClassificationFacet) {
    this.facility = facility;
    this.models = [];

    this.options = options;
    //this.options.useTopLevel = true;

    this.facetsEffects = new FacetVizEffects(this.facility, this.options);

    this.facetDefs = getDefaultFacetList(facility, wantClassificationFacet).map((ctor) => new ctor());
    this.facetDefs.forEach((fDef) => fDef.init({}, this.facility, this.options));

    this.mergedFacets = this.facetDefs.map(() => []);

    this.hiddenCategories = []; //or optionally [RC.Rooms, RC.MEPSpaces, RC.Levels], set via the Edit Filters UI

    this.boundOnIsolationChanged = this.onIsolationChanged.bind(this);
    this.boundOnDocumentsChanged = this.onDocumentsChanged.bind(this);
    this.boundOnSystemsChanged = this.onSystemsChanged.bind(this);
    this.boundOnPreferenceChanged = this.onPreferenceChanged.bind(this);

    this.settings = this.facetDefs; //TODO: temporary assignment in order to not break the dt-client which uses this variable
    this.facetsNeedRecalculation = true;

    this.viewRestoreInProgress = false;

    this.setEventTarget(this.facility.eventTarget);
    this.eventTarget.addEventListener(dte.DT_DOCUMENTS_CHANGED_EVENT, this.boundOnDocumentsChanged);
    this.eventTarget.addEventListener(dte.DT_SYSTEMS_CHANGED_EVENT, this.boundOnSystemsChanged);

    // speed up facet creation by kicking off async fetching of revit categories (will be used by FacetRvtCategories)
    DtConstants.getRevitCategories();
    DtConstants.getClassificationsLibrary();
    this.viewId = undefined;

    this.lastFacetRecalcTime = 0;
    this.pendingRecalcModels = new Set();
    this.pendingAddModels = new Set();

    // manually hidden/isolated elements as tracked in onIsolationChanged and honored by updateIsolation
    this.hidden = new Map();
    this.isolated = new Map();
  }

  /**
   * Update FacetsManager settings
   * @param {import("./Facets").FacetSettings[]} newSettings list of facet settings.
   */
  async setSettings(newSettings, hiddenCategories, useTopLevel) {
    if (!newSettings?.length) {
      return;
    }

    // Create current facet definitions maps. This will be used to restore state of existing facetdef when updating settings
    const existingFacetDefsById = {};
    for (let fd of this.facetDefs) {
      existingFacetDefsById[fd.id] = fd;
    }

    const newFacetDefs = [];

    //Enforce presence of facets which are required for parts of the UI to function,
    //but mark them as hidden, so they don't show in the Filters panel

    //Make sure the Source models facet exists and is first
    if (newSettings[0].id !== FacetTypes.models) {
      this.facetDefs[0].hidden = true;
    } else {
      this.facetDefs[0].hidden = newSettings[0].hidden;
    }
    newFacetDefs.push(this.facetDefs[0]);

    //Make sure the Levels facet exists
    const facetLevels = existingFacetDefsById[FacetTypes.levels];
    if (!facetLevels) {
      console.warn("unexpected situation -- levels facet does not exist");
    }
    let levelsIdx = newSettings.findIndex((fd) => fd.id === FacetTypes.levels);
    if (levelsIdx === -1) {
      levelsIdx = newFacetDefs.push(facetLevels) - 1;
      facetLevels.hidden = true;
    } else {
      facetLevels.hidden = newSettings[levelsIdx].hidden;
    }

    //Make sure the spaces facet exists -- there is a slight complication here in that
    //we add the spaces facet after the levels facet, which can be located anywhere in the input list
    const facetSpaces = existingFacetDefsById[FacetTypes.spaces];
    if (!facetSpaces) {
      console.warn("unexpected situation -- spaces facet does not exist");
    }
    let spacesIdx = newSettings.findIndex((fd) => fd.id === FacetTypes.spaces);
    if (spacesIdx === -1) {
      facetSpaces.hidden = true;
      //If we already added the Levels facet above, we can add spaces here,
      //otherwise we do it in the loop below
      if (levelsIdx === 1) {
        newFacetDefs.push(facetSpaces);
      }
    } else {
      facetSpaces.hidden = newSettings[spacesIdx].hidden;
    }


    for (let i = 0; i < newSettings.length; i++) {

      const settings = newSettings[i];

      //Was already specially handled above
      if (settings.id === FacetTypes.models) continue;

      // reuse existing facet definitions to keep current state or instantiate new one
      let facetDef = existingFacetDefsById[settings.id] || deserializeFacet(settings.id);
      if (!facetDef) {
        continue;
      }

      await facetDef.init(settings, this.facility, this.options);

      newFacetDefs.push(facetDef);

      //Add the hidden Spaces facet after the Levels facet if that
      //wasn't already done above
      if (facetDef.id === FacetTypes.levels && spacesIdx === -1) {
        newFacetDefs.push(facetSpaces);
      }

    }

    //Check if facet defs are changing
    //If not, we can bypass facet recalculation.
    //TODO: we can do even better by only recalculating facets that are being added
    let facetsAreChanging = false;
    if (newFacetDefs.length !== this.facetDefs.length) {
      facetsAreChanging = true;
    }
    if (!facetsAreChanging) {
      for (let i = 0; i < this.facetDefs.length; i++) {
        if (this.facetDefs[i] !== newFacetDefs[i]) {
          facetsAreChanging = true;
          break;
        }
      }
    }

    //Check if element flag/category filter is changing
    if (typeof hiddenCategories !== "undefined") {
      if (!smallArraysEqual(this.hiddenCategories, hiddenCategories)) {
        this.hiddenCategories = hiddenCategories;
        facetsAreChanging = true;
      }
    }

    //Note that the options object is held as pointer
    //by the FacetLevels (and all other Facets) so setting the
    //option here and then recalculating the facet will update the facet counts
    if (typeof useTopLevel !== "undefined") {
      if (!!this.options.useTopLevel !== !!useTopLevel) {
        this.options.useTopLevel = useTopLevel;
        facetsAreChanging = true;
      }
    }


    if (this.facetsNeedRecalculation || facetsAreChanging) {
      this.facetsNeedRecalculation = false;

      if (this.models.length) {
        await this.updateFacetsAttributes(newFacetDefs, this.models);
        return this.recalculateModelFacets(newFacetDefs, this.models, true, true);
      } else {
        //No models in the scene, probably we are getting called
        //by view restoring code, before any model is loaded/added yet.
        //Just remember the new settings and let the first call to addModel()
        //do the recalculation.
        this.facetDefs = newFacetDefs;
      }
    }
  }

  setViewer(viewer) {

    if (this.viewer) {
      this.viewer.removeEventListener(et.PREF_CHANGED_EVENT, this.boundOnPreferenceChanged);
    }

    this.viewer = viewer;
    this.facetsEffects.setViewer(viewer);

    this.viewer.addEventListener(et.PREF_CHANGED_EVENT, this.boundOnPreferenceChanged);
  }

  /**
   * Sets event target for Dt events
   * @param {EventDispatcher} eventTarget
   */
  setEventTarget(eventTarget) {
    this.eventTarget = eventTarget;
  }

  highlightLocations(forceUpdate = false) {

    this.facetsEffects.clearSpaceOverlays();

    if (!this.facetsEffects.highlightingEnabled) {
      return;
    }

    //Check if we have a filter of any kind (except source models), in which case we will show the
    //building outline for context.
    let hasFilter = false;
    for (let i = 1; i < this.facetDefs.length; i++) {
      if (this.facetDefs[i].filter.size > 0) {
        hasFilter = true;
        break;
      }
    }

    const spacesIdx = this.getFacetDefIndex(FacetTypes.spaces);
    if (spacesIdx !== -1) {

      for (let model of this.models) {

        let roomFacets = this.mergedFacets[spacesIdx];

        for (let i = 0; i < roomFacets.length; i++) {

          let facetProps = roomFacets[i];

          //Each room facet belongs to a certain model (since it's associated with room geometry in that model),
          //so we have to make sure the current room refers to the current model being processed.
          if (facetProps.roomSrcModel !== model) {
            continue;
          }

          // Add room overlays regardless of the model's visibility,
          // but skip selection for room elements of hidden models
          if (facetProps.selected) {
            this.facetsEffects.addSpaceFilterViz(facetProps, forceUpdate);
          }
        }
      }
    }

    const levelsIdx = this.getFacetDefIndex(FacetTypes.levels);
    if (levelsIdx !== -1) {

      let levelsFacets = this.mergedFacets[levelsIdx];
      let selectedLevels = [];

      for (let i = 0; i < levelsFacets.length; i++) {
        let facetProps = levelsFacets[i];
        if (facetProps.selected) {
          selectedLevels.push(facetProps);
        }
      }

      //In case a space is filtered by itself (without a level filter present)
      //we also show the building outline for context.
      if (selectedLevels.length || hasFilter) {
        for (let i = 0; i < levelsFacets.length; i++) {
          let facetProps = levelsFacets[i];
          // Add room overlays regardless of the model's visibility,
          // but skip selection for room elements of hidden models
          if (facetProps.selected) {
            this.facetsEffects.addFloorEdgeHighlight(facetProps);
          } else {
            this.facetsEffects.addFloorBoundaryViz(facetProps);
          }
        }

        //Add floor boundaries to levels that are filtered
        this.facetsEffects.addFloorplan(selectedLevels);
      }

    }
  }

  skipElement(flags, catId) {

    if (this.hiddenCategories.includes(catId)) {
      return true;
    }

    if ((flags & KeyFlags.Logical) === 0) {
      return false;
    }

    //Don't skip elements whose flags are "unknown" -- sometimes the NWD importer will classify
    //elements that have geometry as "unknown" and we don't want to ignore those for filtering purposes
    if (flags === ElementFlags.Unknown) {
      return false;
    }

    //if Logical element, include streams, levels, and generic assets since we want to show their properties.
    return flags !== ElementFlags.Stream && flags !== ElementFlags.Level && flags !== ElementFlags.GenericAsset;
  }

  async addModel(model, visibleInFilters) {
    await model.waitForLoad(visibleInFilters, true);

    await this.updateFacetsAttributes(this.facetDefs, [model]);

    //Multiple callers can be waiting on this, some of which can trigger a recalculation again,
    //so we can't be sure inProgressRecalc won't be still set by someone else after waiting for it once.
    while (this.inProgressRecalc) {
      await this.inProgressRecalc;
    }

    //Note that there cannot be any async calls from this point down to the call to queueFacetRecalc below.

    this.models.push(model);
    if (visibleInFilters) {
      this.facetDefs[MODEL_FACET_DEFS_IDX].filter.add(model.urn());
    }


    //Also recalculate facets for models that have external references to the newly added model
    //Currently this matters for the rooms facet and systems facets
    let toUpdate = new Set([model]);
    {
      const systemsFacet = this.getFacetDef(FacetTypes.mepSystems);
      for (let m of this.models) {
        if (m === model) {
          continue;
        }

        if (m.hasExternalRefs(model.urn())) {
          toUpdate.add(m);
        } else
        if (model.isDefault() && systemsFacet) {
          toUpdate.add(m);
        }
      }
      systemsFacet?.clearCachedExternalDataForModel(model);
    }

    if (this.facility.modelsToAbort.has(model)) {
      return;
    }

    let isLast = this.models.length === this.facility.getModels().length;
    await this.queueFacetRecalc(toUpdate, model, isLast);

    if (this.models.length === 1) {
      this.viewer.addEventListener(et.AGGREGATE_ISOLATION_CHANGED_EVENT, this.boundOnIsolationChanged);
      this.viewer.addEventListener(et.AGGREGATE_HIDDEN_CHANGED_EVENT, this.boundOnIsolationChanged);
    }

    if (isLast) {
      this.isLoaded = true;
      this.eventTarget.dispatchEvent({
        model,
        type: dte.DT_FACETS_LOADED
      });
    }
  }

  /**
   * Queue model facet recalculation, which happens lazily (if forced, 20% of all models are pending or
   * 5 seconds have passed since the last recalculation)
   * @param {Iterable.<DtModel>} toUpdate - models that require recalculation
   * @param {DtModel} addedModel - model that was added triggering this recalculation
   * @param {boolean} force
   */
  async queueFacetRecalc(toUpdate, addedModel, force) {

    if (this.inProgressRecalc) {
      console.warn("unexpected pending recalc");
    }

    this.pendingAddModels.add(addedModel);

    for (let model of toUpdate) {
      this.pendingRecalcModels.add(model);
    }

    let now = Date.now();
    let timeDelta = now - this.lastFacetRecalcTime;
    let tooMany = this.pendingAddModels.size / this.facility.getModels().length > 0.2;

    if (force || tooMany || timeDelta > 5000) {

      this.lastFacetRecalcTime = now;

      let toUpdate = Array.from(this.pendingRecalcModels);
      this.pendingRecalcModels.clear();

      let toAdd = Array.from(this.pendingAddModels);
      this.pendingAddModels.clear();

      //console.log("recalc facets", toUpdate.length, force, timeDelta);

      let resolve, reject;
      this.inProgressRecalc = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
      });

      this.recalculateModelFacets(null, toUpdate, this.viewRestoreInProgress, this.viewRestoreInProgress).then(resolve).catch(reject);

      await this.inProgressRecalc;
      this.inProgressRecalc = null;

      for (let model of toAdd) {

        //Model could have been unloaded meanwhile
        if (!model.getData()) {
          continue;
        }

        //Load any room geometries for this newly added model.
        //We want this to happen after recalculateModelFacets(), which can itself trigger loading
        //of element geometries, that are more important than rooms (since rooms come into play only later
        //if the user interacts with the rooms filter).
        model.loadRoomFragments();
      }
    }
  }

  removeModel(model, skipUpdate) {
    const idx = this.models.indexOf(model);
    if (idx !== -1) {
      this.facetDefs.forEach((facetDef) => facetDef.clearCachedExternalDataForModel(model));
      this.facetsEffects.invalidateFloorHighlights();
      this.models.splice(idx, 1);

      if (!skipUpdate) {
        this.updateFacets();
      }
    }

    if (this.models.length === 0) {
      this.viewer.removeEventListener(et.AGGREGATE_ISOLATION_CHANGED_EVENT, this.boundOnIsolationChanged);
      this.viewer.removeEventListener(et.AGGREGATE_HIDDEN_CHANGED_EVENT, this.boundOnIsolationChanged);
      this.facetsNeedRecalculation = true;
    }

    if (this.models.length < this.facility.getModels(true).length) {
      this.isLoaded = false;
    }
  }

  /**
   * @param nFacetDefs {FacetDef[]|null}
   * @param models {DtModel[]}
   */
  async recalculateModelFacets(nFacetDefs, models, skipFacetsUpdate, skipIsolationUpdate) {

    let foundModels;

    if (models === this.models) {
      foundModels = models;
    } else {
      foundModels = models.filter((m) => this.models.includes(m));
      if (foundModels.length === 0) {
        return;
      }
    }

    nFacetDefs = nFacetDefs || this.facetDefs;

    // prune attribute facets that point to an attribute that was hidden (e.g. due to a template update)
    nFacetDefs = nFacetDefs.filter((facetDef) => !facetDef.attribute || !facetDef.attribute.isHidden());

    for (let model of foundModels) {
      // looping over all elements and set their visibility
      const dbId2flags = model.getData().dbId2flags;
      const dbId2catId = model.getData().dbId2catId;
      const vm = model.visibilityManager;
      const na = model.getInstanceTree().nodeAccess;
      for (let dbId = 1; dbId < dbId2flags.length; ++dbId) {
        const flags = dbId2flags[dbId];
        const catId = dbId2catId[dbId];

        const hiddenFromFilters = this.skipElement(flags, catId);
        const isLeafElement = na.getNumFragments(dbId) > 0;

        if (vm && isLeafElement) {
          vm.lockNodeVisible(dbId, false, false);
          vm.setNodeOff(dbId, hiddenFromFilters);
          if (hiddenFromFilters) {
            vm.lockNodeVisible(dbId, true, false);
          }
        }
      }
    }
    // cache external dependencies of the classification process
    await Promise.all(foundModels.flatMap((model) => nFacetDefs.map((facetDef) => facetDef.cacheExternalDataForModel(model))));

    this.facetDefs = nFacetDefs;

    if (!skipFacetsUpdate) {
      this.updateFacets(foundModels.length === 1 ? foundModels[0].urn() : undefined, skipIsolationUpdate, false);
    }

    // redo theming
    let idxCats = -1;
    let idxTandemCats = -1;
    let idxSystemClasses = -1;
    let idxMepSystems = -1;

    this.facetDefs.forEach((facetDef, idx) => {
      if (facetDef.id === FacetTypes.categories) {
        idxCats = idx;
      } else if (facetDef.id === FacetTypes.systemClasses) {
        idxSystemClasses = idx;
      } else if (facetDef.id === FacetTypes.mepSystems) {
        idxMepSystems = idx;
      } else if (facetDef.id === FacetTypes.tandemCategories) {
        idxTandemCats = idx;
      }
    });

    if (idxMepSystems >= 0) {
      this.facetsEffects.setupHighlightBySystem();
    } else if (idxSystemClasses >= 0) {
      this.facetsEffects.setupHighlightBySystemClass();
    } else if (idxTandemCats >= 0) {
      this.facetsEffects.setupHighlightByTandemCategory();
    } else if (idxCats >= 0) {
      this.facetsEffects.setupHighlightByCategory();
    }
  }

  /**
   *
   * @param {import("./Facets").FacetSettings} settings
   * @param {number} insertIndex
   * @returns
   */
  async addFacetDef(settings, insertIndex) {
    const nFacetDefs = this.facetDefs.slice();

    const facetDef = deserializeFacet(settings.id);
    if (!facetDef) {
      return nFacetDefs;
    }

    await facetDef.init(settings, this.facility, this.options);

    await this.updateFacetsAttributes([facetDef], this.models);

    if (typeof insertIndex !== "number" || insertIndex < 0 || insertIndex > this.facetDefs.length) {
      const sysIndex = nFacetDefs.findIndex((v) => {
        return v.id === FacetTypes.tandemCategories;
      });
      if (sysIndex !== -1) {
        insertIndex = sysIndex + 1;
      } else {
        insertIndex = nFacetDefs.length;
      }
    }

    nFacetDefs.splice(insertIndex, 0, facetDef);

    await this.recalculateModelFacets(nFacetDefs, this.models);

    return nFacetDefs;
  }

  setFilterMap(filterMap, mergeIntoExisting, skipUpdate) {
    for (let def of this.facetDefs) {
      if (!mergeIntoExisting) {
        def.filter.clear();
      }
      filterMap[def.id]?.forEach((f) => def.addFilter(f));
    }
    if (!skipUpdate) {
      this.updateFacets();
    }
  }

  /**
   * Generates a map of facet IDs to their corresponding filter sets.
   *
   * @returns {Object.<string, Set>} An object mapping each facet ID to a Set of its filter values.
   */
  getFilterMap() {
    return this.facetDefs.reduce((a, c) => {
      var new_set;
      new_set = new Set([]);
      for (const f of c.filter) {
        if (f === undefined) {
          new_set.add(UNDEFINED);
        } else {
          new_set.add(f);
        }
      }
      a[c.id] = new_set;
      return a;
    }, {});
  }

  deriveFilterMapFromMergedFacets() {
    function dfs(facet, wantWildcards) {
      if (!facet.children?.length) {
        return facet.selected ? { allSelected: true, filters: [facet.id] } : { allSelected: false, filters: [] };
      } else {
        const children = facet.children.map((child) => dfs(child, wantWildcards));
        const selected = facet.selected || facet.ids.size === 0; // NOTE: if an inner node is empty, the UI doesn't explicitly mark it as selected 
        const allSelected = selected && children.every((child) => child.allSelected);
        const filters = wantWildcards && allSelected ? [facet.id + "/*"] : children.flatMap((child) => child.filters);
        return { allSelected, filters };
      }
    }

    const filtersPlus = this.facility.loadContext.filtersPlus;
    return this.mergedFacets.reduce((acc, facets, idx) => {
      const filters = facets.flatMap((facet) => dfs(facet, filtersPlus && this.facetDefs[idx].supportsWildcardFilters()).filters);
      acc[this.facetDefs[idx].id] = new Set(filters);
      return acc;
    }, {});
  }

  /** Removes any filter applied for the facet passed in by ID and updates facets. */
  clearFilter(id, skipUpdate) {
    for (const facet of this.facetDefs) {
      if (facet.id === id) {
        facet.filter.clear();
        break;
      }
    }

    if (!skipUpdate) {
      this.updateFacets();
    }
  }

  /**
   * @deprecated Use setVisibilityById() instead
   */
  setVisibility(facetIdx, valueId, visible, skipUpdate = false) {

    const f = this.facetDefs[facetIdx].filter;
    if (f.has(valueId) !== visible) {
      if (visible) {
        f.add(valueId);
      } else {
        f.delete(valueId);
      }

      if (!skipUpdate) {
        const skipIsolationUpdate = false;
        const urn = facetIdx === MODEL_FACET_DEFS_IDX ? valueId : null;
        this.updateFacets(urn, skipIsolationUpdate, visible);
      }
    }
  }

  setVisibilityById(facetId, valueId, visible, skipUpdate = false) {
    this.setVisibility(this.getFacetDefIndex(facetId), valueId, visible, skipUpdate);
  }

  /**
   * Adjusts a model control's visibility.
   * @param urn Model urn
   * @param visible Visibility
   * @param skipUpdate Skips the facet updates.
   */
  setModelVisibility(urn, visible, skipUpdate = false) {
    this.setVisibility(MODEL_FACET_DEFS_IDX, urn, visible, skipUpdate);
  }

  isVisible(facetIdx, valueId) {
    const f = this.facetDefs[facetIdx].filter;
    return f.size == 0 || f.has(valueId);
  }

  resetVisibility() {
    this.facetDefs.forEach((facetDef) => facetDef.filter.clear());
    this.facetDefs[MODEL_FACET_DEFS_IDX].filter = new Set(this.facility.settings.links.filter((m) => m.on).map((m) => m.modelId));
    this.updateFacets();
    this.eventTarget.dispatchEvent({
      type: dte.DT_FACETS_RESET
    });
  }

  /**
   * Determine whether a model-only filter is applied
   * Eg. when every other facet is either all selected or all deselected
  */
  _modelOnlyFilter() {
    const facets = this.getFacets().slice(1);

    const allDescendantsSelected = (node) => {
      if (!node.selected) return false;
      if (!node.children?.length) return true;

      return node.children.every((child) => allDescendantsSelected(child));
    };

    return this.facetDefs.slice(1).every((facetDef, facetIdx) => {
      if (facetDef.filter.size === 0) return true;

      return facets[facetIdx].every(allDescendantsSelected);
    });
  }

  updateIsolation(urnHint) {
    // the primary model will never be unloaded but might need ot be loaded initially
    // for all other models, missing elements are on-demand loaded
    // and stay around until the entire model gets unchecked, which unloads
    // all its elements.

    const modelOnly = this._modelOnlyFilter();

    //let t0 = Date.now();
    const selections = [];


    for (let m of this.models) {
      const urn = m.urn();
      // because facet filters interact with each other
      // this optimization only works if there are no filters
      // below the model level defined
      if (modelOnly && urnHint && urn != urnHint) {
        continue;
      }
      let hideModel = !this.idsByModelFiltered.has(m);
      if (!this.facility.loadContext.filtersPlus) {
        hideModel = !this.facetDefs[MODEL_FACET_DEFS_IDX].filter.has(m.urn());
      }
      if (hideModel) {
        if (this.facility.isModelVisible(m)) {
          m.selector.clearSelection();
          m.visibilityManager.isolateNone();
          this.viewer.hideModel(m);

          const mergedSpacesFacets = this.getFacet(FacetTypes.spaces) || [];
          const hasSpacesInFacets = m.hasRooms() && mergedSpacesFacets.some((s) => s.roomSrcModel === m);

          // Check if current model has selectable spaces in the filters
          if (!m.isPrimaryModel() && !hasSpacesInFacets) {
            // Keep model elements for highlights/hovering overlays... hoverlays
            m.unloadAllElements();
          }
        }
        continue;
      }

      if (!this.viewer.impl.modelVisible(m.id)) {
        this.viewer.showModel(m);
      }

      const hiddenIds = this.hidden.get(m);

      if (modelOnly) {
        m.loadAllElements();
        const hasIsolationOverride = this.isolated.size > 0;
        if (hasIsolationOverride) {
          const isolatedIds = this.isolated.get(m);
          if (isolatedIds) {
            m.visibilityManager.isolate(isolatedIds);
          } else {
            m.visibilityManager.setAllVisibility(false);
          }
        } else {
          m.visibilityManager.isolateNone();
        }
        if (hiddenIds) {
          m.visibilityManager.hide(hiddenIds);
        }
      } else {
        const sel = m.selector.getSelection().sort(compareNumbers);
        m.selector.clearSelection();

        let ids = this.getIsolationForModel(m);

        if (ids?.length) {

          if (m.isPrimaryModel() && this.viewer.prefs.get(Prefs3D.GHOSTING)) {
            m.loadAllElements();
          } else {
            m.loadElements(ids);
          }

          m.visibilityManager.isolate(ids);
          if (hiddenIds) {
            m.visibilityManager.hide(hiddenIds);
          }

          const selection = sortedArrayIntersection(sel, ids);
          if (selection?.length) {
            selections.push({ model: m, selection });
          }
        } else {
          m.visibilityManager.setAllVisibility(false);
        }


      }
    }

    if (selections.length) {
      this.viewer.impl.selector.setAggregateSelection(selections);
    }

    //let t1 = Date.now();
    //console.log("facet update", t1 - t0);

    this.highlightLocations();

    this.viewer.dispatchEvent({
      type: et.AGGREGATE_ISOLATION_CHANGED_EVENT,
      isolation: this.viewer.getAggregateIsolation(),
      source: this
    });
  }

  // intersection of elements passing all filters and the manual isolation override
  getIsolationForModel(model) {
    let leafIds = this.idsByModelFiltered.get(model);
    if (leafIds) {
      const hasIsolationOverride = this.isolated.size > 0;
      if (hasIsolationOverride) {
        const isolatedIds = this.isolated.get(model);
        if (isolatedIds) {
          leafIds = sortedArrayIntersection(leafIds, isolatedIds);
        } else {
          leafIds = null;
        }
      }
    }
    return leafIds;
  }

  // same as above but in a map for all models
  getIsolation() {
    let result = new Map();
    for (let model of this.models) {
      let isolation = this.getIsolationForModel(model);
      if (isolation) {
        result.set(model, isolation);
      }
    }
    return result;
  }

  // Fast approximation whether the filters only let rooms/spaces pass.
  // There's edge cases where this method is reporting false negatives
  // (like a model with only rooms).
  _areOnlyRoomCategoriesEnabled() {

    for (let i = 0; i < this.facetDefs.length; i++) {
      let facetDef = this.facetDefs[i];
      if (facetDef.id === FacetTypes.tandemCategories) {
        if (facetDef.filter.size > 0) {
          let locationOnly = true;
          for (let code of facetDef.filter) {
            if (!code.startsWith(DtConstants.TC.LocationPrefix)) {
              locationOnly = false;
              break;
            }
          }
          if (locationOnly) {
            return true;
          }
        }
      } else if (facetDef.id === FacetTypes.categories) {
        let filter = facetDef.filter;
        if (filter.size === 1) {
          if (filter.has(RC.Rooms) || filter.has(RC.MEPSpaces)) {
            return true;
          }
        } else if (filter.size === 2) {
          if (filter.has(RC.Rooms) && filter.has(RC.MEPSpaces)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  //Detects if the current filter is filtering for a specific Level
  getLevelFilter() {
    for (let i = 0; i < this.facetDefs.length; i++) {
      let facetDef = this.facetDefs[i];
      if (facetDef.id === FacetTypes.levels) {
        let filter = facetDef.filter;
        //For the moment we only know how to handle
        //single level filters
        if (filter.size === 1) {
          let filterName = Array.from(filter)[0];
          let filteredFacet = this.mergedFacets[i].find((f) => f.id === filterName);
          return { facetDef, mergedFacet: filteredFacet };
        }
      }
    }

    return {};
  }

  collectUnfilteredMergedFacets() {
    const { mergedResult } = this.makeMergedFacets(true);
    for (let fIdx = 0; fIdx < this.facetDefs.length; ++fIdx) {
      mergedResult[fIdx] = this.facetDefs[fIdx].formatMergedFacetNodes(mergedResult[fIdx]);
    }
    return mergedResult;
  }

  updateFacets(urn, skipIsolationUpdate, visible) {
    // TODO: still needed?
    this.facetDefs.forEach((f) => {
      if (f.filter.has(UNDEFINED)) {
        f.filter.delete(UNDEFINED);
        f.filter.add(undefined);
      }
    });

    const addedModel = visible ? urn : undefined;

    const { mergedResult, idsByModelFiltered, idsInMultipleFacets } = this.makeMergedFacets();

    for (let fIdx = 0; fIdx < this.facetDefs.length; ++fIdx) {
      mergedResult[fIdx] = this.facetDefs[fIdx].formatMergedFacetNodes(mergedResult[fIdx]);
    }
    this.mergedFacets = mergedResult;

    this.idsByModelFiltered = idsByModelFiltered;
    this.idsInMultipleFacets = idsInMultipleFacets;

    if (this.viewer) {
      if (!skipIsolationUpdate) {
        this.updateIsolation(urn);
      } else {
        this.highlightLocations();
      }

      this.facetsEffects.makeRoomsOpaque(this._areOnlyRoomCategoriesEnabled());

      if (this.options.useTopLevel) {
        let { facetDef, mergedFacet } = this.getLevelFilter();
        this.facetsEffects.sectionVerticalElements(facetDef, mergedFacet);
      }

      this.eventTarget.dispatchEvent({
        type: dte.DT_FACETS_UPDATED,
        addedModel
      });
    }


    return this.mergedFacets;
  }

  /**
   * Map attributes from their hashes
   */
  async updateFacetsAttributes(facetDefs, models) {
    let hash2Attrs; // cross-model attributes map
    for (let i = 0; i < facetDefs.length; i++) {
      const facetDef = facetDefs[i];
      if (facetDef.settings.attributeHash) {
        //Collect attributes from all models
        if (!hash2Attrs) {
          hash2Attrs = {};
          const modelsHash2Attrs = await Promise.all(models.map((m) => m.getHash2Attr()));

          for (let i = 0; i < modelsHash2Attrs.length; i++) {
            for (let hash in modelsHash2Attrs[i]) {
              let attr = modelsHash2Attrs[i][hash];
              hash2Attrs[hash] = attr;
            }
          }
        }

        let attribute = hash2Attrs[facetDef.settings.attributeHash];
        if (attribute) {
          facetDef.setAttribute(attribute);
        } else {
          //Attempt to match using old (incorrect) attribute hash that is of the form [psetUuid][paramUuid][dataType]
          //instead of [paramUuid][dataType]. This should only happen when trying to load old saved views with custom facets
          let firstBrace = facetDef.settings.attributeHash.indexOf("]");
          if (firstBrace > 0) {
            attribute = hash2Attrs[facetDef.settings.attributeHash.slice(firstBrace + 1)];
            if (attribute) {
              console.log("Matched parameter using old hash function", facetDef.settings.attributeHash);
              facetDef.setAttribute(attribute);
            }
          }

        }
      }
    }
  }

  getFacets() {
    return this.mergedFacets;
  }

  getFacetDefs() {
    return this.facetDefs;
  }

  onIsolationChanged(event) {
    // skipping events we fired ourselves
    if (event.source === this) {
      return;
    }

    if (event.type === et.AGGREGATE_ISOLATION_CHANGED_EVENT) {
      if (Array.isArray(event.isolation)) {
        this.isolated.clear();
        this.hidden.clear();
        if (event.isolation.length === 0) {
          // show all
          for (let i = 1; i < this.facetDefs.length; ++i) {
            this.facetDefs[i].filter.clear();
          }
          this.updateFacets(null, true);
        } else {
          for (let { model, ids } of event.isolation) {
            this.isolated.set(model, ids.sort(compareNumbers));
          }

          this.eventTarget.dispatchEvent({ type: dte.DT_FACETS_UPDATED });
        }
      }
    } else if (event.type === et.AGGREGATE_HIDDEN_CHANGED_EVENT) {
      for (let { model, ids } of event.hidden) {
        if (!this.hidden.has(model)) {
          this.hidden.set(model, ids);
        } else {
          const hiddenIds = this.hidden.get(model);
          this.hidden.set(model, hiddenIds.concat(ids));
        }

        this.eventTarget.dispatchEvent({ type: dte.DT_FACETS_UPDATED });
      }
    }
  }


  setHiddenElements({ model, ids }) {
    this.hidden.set(model, ids);
    this.updateIsolation(model.urn());
    this.eventTarget.dispatchEvent({ type: dte.DT_FACETS_UPDATED });
  }
  setIsolatedElements({ model, ids }) {
    this.isolated.set(model, ids.sort(compareNumbers));
    this.updateIsolation(model.urn());
    this.eventTarget.dispatchEvent({ type: dte.DT_FACETS_UPDATED });
  }

  hasFilterOverrides() {
    return this.hidden.size > 0 || this.isolated.size > 0;
  }

  clearFilterOverrides() {
    this.hidden.clear();
    this.isolated.clear();
    this.updateIsolation();
  }

  getFilterOverrides() {
    return {
      hidden: this.hidden,
      isolated: this.isolated
    };
  }


  onDocumentsChanged(event) {
    if (event?.deletedDocumentId?.length) {
      // When adding a custom facet on documents references, we need to make sure that document deletion updates facet state as well.
      // For now we do it locally within a browser session. In general, it might be useful to send events from the server to update all interested clients, but it looks like too much of an effort for now, given that this is an exotic edge case.
      // only do it if deleted document would affect the actual facets
      for (const facet of this.mergedFacets) {
        const affectedDocument = facet.find((node) => node.id === event.deletedDocumentId);
        if (affectedDocument) {
          this.recalculateModelFacets(null, this.models);
          break;
        }
      }
    }
  }

  onSystemsChanged(event) {
    if (event.type === dte.DT_SYSTEMS_CHANGED_EVENT && event.change.ctype === DtConstants.ChangeTypes.DeleteSystems ||
    event.type === dte.DT_SYSTEMS_CHANGED_EVENT && event.change.ctype === DtConstants.ChangeTypes.UpdateSystems) {
      for (let m of this.models) {
        this.getFacetDef(FacetTypes.mepSystems)?.clearCachedExternalDataForModel(m);
      }

      this.recalculateModelFacets(null, this.models);
    }
  }

  onPreferenceChanged(event) {

    if (event.name === Prefs3D.SELECTION_MODE) {
      this.recalculateModelFacets(null, this.models);
    }
  }

  getFacetDefIndex(facetType) {
    for (let i = 0; i < this.facetDefs.length; i++) {
      if (this.facetDefs[i].id === facetType) {
        return i;
      }
    }

    return -1;
  }
  getFacetDef(facetType) {
    const idx = this.getFacetDefIndex(facetType);
    if (idx === -1) {
      return null;
    }
    return this.facetDefs[idx];
  }

  getFacet(facetType) {
    const idx = this.getFacetDefIndex(facetType);
    if (idx === -1) {
      return null;
    }
    return this.mergedFacets[idx];
  }

  makeMergedFacets(ignoreFilter) {
    const log = false;
    log && console.group("makeMergedFacets");
    // console.profile("makeMergedFacets");
    log && console.time("makeMergedFacets");
    log && console.time("collect elements");

    // Collect all relevant elements grouped by model
    let idsByModel = new Map();
    let count = 0;
    for (let model of this.models) {
      const dbId2flags = model.getData().dbId2flags;
      const dbId2catId = model.getData().dbId2catId;

      const ids = [];

      for (let dbId = 1; dbId < dbId2flags.length; ++dbId) {
        const flags = dbId2flags[dbId];
        const catId = dbId2catId[dbId];

        const hiddenFromFilters = this.skipElement(flags, catId);
        if (!hiddenFromFilters) {
          ids.push(dbId);
        }
      }

      idsByModel.set(model, ids);
      count += ids.length;
    }
    log && console.timeEnd("collect elements");
    log && console.log("#elements:", count);

    const mergedResult = [];
    const idsInMultipleFacets = [];
    const options = {
      useLastObject: this.viewer.getSelectionMode() === SelectionMode.LAST_OBJECT,
      ignoreFilter
    };
    for (let facetDef of this.facetDefs) {
      log && console.time("batchClassify " + facetDef.id);

      const res = facetDef.batchClassify(idsByModel, options);
      log && console.timeEnd("batchClassify " + facetDef.id);

      mergedResult.push(res.facets);
      idsByModel = res.idsByModelFiltered;
      idsInMultipleFacets.push(res.idsInMultipleFacetsByModel);
    }

    log && console.timeEnd("makeMergedFacets");
    //console.profileEnd("makeMergedFacets");
    log && console.groupEnd();

    return { mergedResult, idsByModelFiltered: idsByModel, idsInMultipleFacets };
  }
}